<script setup lang="ts">
import { usePaneHeight } from '@/composeables/use-pane-height';
import {
  collapseSoleMount,
  useProjectMounts,
} from '@/composeables/use-project-mounts';
import {
  PhFolderDashed,
  PhLockSimple,
  PhWarningCircle,
} from '@phosphor-icons/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  FileManager,
  FileManagerNotice,
  Spinner,
} from './sub-components/file-manager';
import { OneDriveSyncButton } from './sub-components/onedrive-sync';

const { t } = useI18n();
const route = useRoute();

const projectId = computed(() => String(route.params.id));

const {
  mounts: projectMounts,
  syncTargets,
  status,
} = useProjectMounts({
  prefixOf: (_site, spec) => `/${spec.title}`,
  filter: (site) => site.projectId === projectId.value,
  probe: true,
});

const mounts = computed(() => collapseSoleMount(projectMounts.value));

const syncTarget = computed(() =>
  syncTargets.value.find((entry) => entry.projectId === projectId.value),
);

// Sized to the exact box <FileManager> would have occupied, so a notice sits where the pane would be.
const { paneRef, paneHeight } = usePaneHeight();
</script>

<template>
  <template v-if="status === 'available'">
    <OneDriveSyncButton :target="syncTarget" :mounts="projectMounts" />
    <FileManager :mounts="mounts" />
  </template>
  <div v-else ref="paneRef" :style="{ height: `${paneHeight}px` }">
    <FileManagerNotice v-if="status === 'loading'">
      <template #badge><Spinner /></template>
    </FileManagerNotice>
    <FileManagerNotice
      v-else-if="status === 'unprovisioned'"
      :icon="PhFolderDashed"
      :message="t('documentPane.notProvisionedMessage')"
    />
    <FileManagerNotice
      v-else-if="status === 'no-access'"
      :icon="PhLockSimple"
      :message="t('documentPane.noAccessMessage')"
    />
    <FileManagerNotice
      v-else
      :icon="PhWarningCircle"
      :message="t('documentPane.loadErrorMessage')"
    />
  </div>
</template>
